/**
 * lesson-route-table.ts
 *
 * Single source of truth mapping each wired KP id → the lesson runner function
 * that knows how to play it. Used by `main.ts onSelectKP` to dispatch a click
 * on a KP card to the right runner without a 50+ if-else chain.
 *
 * Invariants enforced by `tests/learn/lesson-route-table.test.ts`:
 *   1. Every key in `KP_ROUTE` exists in `CURRICULUM` (no typos / dead routes)
 *   2. `WIRED_LESSON_KPS` set in `curriculum/wired-kps.ts` equals `Object.keys(KP_ROUTE)`
 *   3. Each runner accepts the canonical `(container, kpId, onDone)` shape
 *
 * Adding a new wired KP = add ONE entry here. The route table is the wire.
 */
import type { LearnDoneCallback } from './app';

import { runMathMul2x1Lesson } from './app';
import { runMathMul3x1Lesson } from './lessons/math-mul-3x1';
import { runMathDivNoRemainderLesson } from './lessons/math-div-no-remainder';
import { runMathDivMulRelationLesson } from './lessons/math-div-mul-relation';
import { runChineseIdiomBasicLesson } from './lessons/chinese-idiom-basic';
import { runChinesePhoneticToneUpLesson } from './lessons/chinese-phonetic-tone-up';

import { runChineseLesson } from './chinese-lesson';
import { runEnglishLesson } from './english-lesson';
import { runNatureLesson } from './nature-lesson';
import { runSocialLesson } from './social-lesson';
import { runMathLesson } from './math-lesson';

export type LessonRunner = (
  container: HTMLElement,
  kpId: string,
  onDone: LearnDoneCallback,
) => void | Promise<void>;

/**
 * Adapter for legacy lessons that ignore the kpId argument (their KP is hardcoded).
 * The container/onDone shape stays the same so the dispatcher is uniform.
 */
function bindLegacy(fn: (container: HTMLElement, onDone: LearnDoneCallback) => void | Promise<void>): LessonRunner {
  return (container, _kpId, onDone) => fn(container, onDone);
}

/**
 * KP id → runner. Order is grouped by subject for readability; runtime lookup is O(1).
 * Adding a new wired KP: add one entry here AND add the same id to WIRED_LESSON_KPS.
 */
export const KP_ROUTE: Record<string, LessonRunner> = {
  // ── math ───────────────────────────────────────────────────────────
  // The 4 banked math KPs share a single overlay (runMathMul2x1Lesson)
  // which routes internally by kpId; the other 3 use dedicated wrappers.
  'math.mul.2x1': bindLegacy(runMathMul2x1Lesson),
  'math.mul.2x2': bindLegacy(runMathMul2x1Lesson),
  'math.div.remainder': bindLegacy(runMathMul2x1Lesson),
  'math.word.two-step': bindLegacy(runMathMul2x1Lesson),
  'math.mul.3x1': bindLegacy(runMathMul3x1Lesson),
  'math.div.no-remainder': bindLegacy(runMathDivNoRemainderLesson),
  'math.div.mul-relation': bindLegacy(runMathDivMulRelationLesson),

  // ── math via generic dispatcher (task #41 Phase B) ────────────────
  // 23 generator-backed KPs (parametric infinite questions)
  'math.calc.addsub-vertical': runMathLesson,
  'math.number.thousand': runMathLesson,
  'math.fraction.compare': runMathLesson,
  'math.decimal.basic': runMathLesson,
  'math.decimal.addsub': runMathLesson,
  'math.area.rectangle': runMathLesson,
  'math.time.duration': runMathLesson,
  'math.time.clock': runMathLesson,
  'math.number.10000': runMathLesson,
  'math.place.value.4digits': runMathLesson,
  'math.add.carry.multi': runMathLesson,
  'math.sub.borrow.multi': runMathLesson,
  'math.mul.2x1.review': runMathLesson,
  'math.div.1digit.no-rem': runMathLesson,
  'math.time.elapsed': runMathLesson,
  'math.large.number.4up': runMathLesson,
  'math.add.sub.multi.4up': runMathLesson,
  'math.mul.2x2.4up': runMathLesson,
  'math.mul.3x1.4up': runMathLesson,
  'math.word.two-step.4up': runMathLesson,
  'math.frac.add.same-denom.4up': runMathLesson,
  'math.decimal.2place.4up': runMathLesson,
  'math.time.complex.4up': runMathLesson,
  // 12 bank-backed KPs (handwritten 50Q each)
  'math.fraction.basic': runMathLesson,
  'math.length.cm': runMathLesson,
  'math.weight.kg': runMathLesson,
  'math.angle.basic': runMathLesson,
  'math.shape.rectangle': runMathLesson,
  'math.shape.circle': runMathLesson,
  'math.stats.bar': runMathLesson,
  'math.stats.table': runMathLesson,
  'math.frac.unit': runMathLesson,
  'math.volume.basic.4up': runMathLesson,
  'math.stats.bar.4up': runMathLesson,
  // stage6 wire: meow911 段考真題池 — #250 6 個小 bank 合併進 misc-pool 後
  // 保留單一綜合題庫 KP(含 ch2-ch7 + 跨單元應用)
  'math.meow911.misc': runMathLesson,

  // ── chinese (康軒) ─────────────────────────────────────────────────
  'chinese.zhuyin.basic': runChineseLesson,
  'chinese.phonetic.tone': runChineseLesson,
  'chinese.vocab.usage': runChineseLesson,
  'chinese.character.radical': runChineseLesson,
  'chinese.segment.summary': runChineseLesson,
  'chinese.sentence.pattern': runChineseLesson,
  'chinese.comprehension.basic': runChineseLesson,
  'chinese.comprehension.explain': runChineseLesson,
  'chinese.writing.guided': runChineseLesson,
  'chinese.writing.diary': runChineseLesson,
  'chinese.punctuation.basic': runChineseLesson,
  'chinese.rhetoric.basic': runChineseLesson,
  'chinese.idiom.basic': bindLegacy(runChineseIdiomBasicLesson),
  'chinese.culture.community': runChineseLesson,
  'chinese.phonetic.tone.up': bindLegacy(runChinesePhoneticToneUpLesson),
  'chinese.vocab.daily.up': runChineseLesson,
  'chinese.sentence.simple.up': runChineseLesson,
  'chinese.comprehension.short.up': runChineseLesson,
  'chinese.writing.fillin.up': runChineseLesson,
  // 三下 by-lesson banks (spec #76)
  'chinese.l01.xuyuan': runChineseLesson,
  'chinese.l02.xiayushihou': runChineseLesson,
  'chinese.l03.yujianmeiruunainai': runChineseLesson,
  'chinese.l04.gongjiangzhizu': runChineseLesson,
  'chinese.l05.xuetianshukai': runChineseLesson,
  'chinese.l06.shenqimima': runChineseLesson,
  'chinese.l07.youtonghua': runChineseLesson,
  // 三下 by-lesson banks L08-L12 (spec #82)
  'chinese.l08.daziranmeishuguan': runChineseLesson,
  'chinese.l09.taiwandeshanjiaoyu': runChineseLesson,
  'chinese.l10.yufuhejinyu': runChineseLesson,
  'chinese.l11.congmingdeshulu': runChineseLesson,
  'chinese.l12.haiyaodiejici': runChineseLesson,
  // stage6 wire: meow911 段考真題池 — #250 8 個小 bank 合併進 misc-pool 後
  // 保留 ≥ 15Q 的識字部首 / 句型段落 + 綜合 misc-pool
  'chinese.character.radical.meow911': runChineseLesson,
  'chinese.sentence.pattern.meow911': runChineseLesson,
  'chinese.meow911.misc': runChineseLesson,

  // ── english (翰林) ─────────────────────────────────────────────────
  'english.alphabet.phonics': runEnglishLesson,
  'english.phonics.basic': runEnglishLesson,
  'english.vocab.family': runEnglishLesson,
  'english.vocab.school': runEnglishLesson,
  'english.vocab.food': runEnglishLesson,
  'english.vocab.animals': runEnglishLesson,
  'english.vocab.colors': runEnglishLesson,
  'english.numbers.basic': runEnglishLesson,
  'english.numbers.ordinal': runEnglishLesson,
  'english.grammar.questions': runEnglishLesson,
  'english.grammar.statements': runEnglishLesson,
  'english.dialogue.daily': runEnglishLesson,
  'english.dialogue.classroom': runEnglishLesson,
  'english.alphabet.review.up': runEnglishLesson,
  'english.vocab.school.up': runEnglishLesson,
  'english.vocab.colors.up': runEnglishLesson,
  'english.greetings.intro.up': runEnglishLesson,
  // stage6 wire: meow911 段考真題池 — #250 4 個小 bank 合併進 misc-pool 後
  // 保留 ≥ 15Q 的 ch5 句型 + 綜合 misc-pool
  'english.grammar.questions.meow911': runEnglishLesson,
  'english.meow911.misc': runEnglishLesson,

  // ── nature (南一) ──────────────────────────────────────────────────
  'nature.plant.lifecycle': runNatureLesson,
  'nature.plant.growth': runNatureLesson,
  'nature.animal.classify': runNatureLesson,
  'nature.animal.adapt': runNatureLesson,
  'nature.water.states': runNatureLesson,
  'nature.water.temp': runNatureLesson,
  'nature.weather.seasons': runNatureLesson,
  'nature.weather.measure': runNatureLesson,
  'nature.magnet.basic': runNatureLesson,
  'nature.machine.basic': runNatureLesson,
  'nature.force.deform': runNatureLesson,
  'nature.sound.light.basic': runNatureLesson,
  'nature.light.basic': runNatureLesson,
  'nature.rock.basic': runNatureLesson,
  'nature.observation.basic.up': runNatureLesson,
  'nature.water.air.up': runNatureLesson,
  'nature.kitchen.science.up': runNatureLesson,
  'nature.light.shadow.up': runNatureLesson,
  // stage6 wire: meow911 段考真題池 — #250 6 個小 bank 合併進 misc-pool 後
  // 保留 ≥ 15Q 的 ch1 植物 / ch3 水三態 + 綜合 misc-pool
  'nature.plant.lifecycle.meow911': runNatureLesson,
  'nature.water.states.meow911': runNatureLesson,
  'nature.meow911.misc': runNatureLesson,

  // ── social (翰林) ──────────────────────────────────────────────────
  'social.community.basic': runSocialLesson,
  'social.family.roles': runSocialLesson,
  'social.taiwan.geography': runSocialLesson,
  'social.local.environment': runSocialLesson,
  'social.hometown.basic': runSocialLesson,
  'social.custom.festival': runSocialLesson,
  'social.life.rule': runSocialLesson,
  'social.traffic.basic': runSocialLesson,
  'social.environment.basic': runSocialLesson,
  'social.self.family.up': runSocialLesson,
  'social.school.life.up': runSocialLesson,
  'social.time.place.up': runSocialLesson,
  'social.rules.basic.up': runSocialLesson,
  // stage6 wire: meow911 段考真題池 — #250 1 個小 bank (ch4) 合併進 misc-pool 後
  'social.community.basic.meow911': runSocialLesson,
  'social.taiwan.geography.meow911': runSocialLesson,
  'social.hometown.basic.meow911': runSocialLesson,
  'social.traffic.basic.meow911': runSocialLesson,
  'social.environment.basic.meow911': runSocialLesson,
  'social.meow911.misc': runSocialLesson,
};

/**
 * Dispatch a KP click to its runner. Returns true if a runner was invoked,
 * false if the KP is not wired (caller should treat as no-op or wip).
 */
export function dispatchLesson(
  container: HTMLElement,
  kpId: string,
  onDone: LearnDoneCallback,
): boolean {
  const runner = KP_ROUTE[kpId];
  if (!runner) return false;
  void runner(container, kpId, onDone);
  return true;
}

export const ROUTED_KP_IDS: readonly string[] = Object.freeze(Object.keys(KP_ROUTE));
